// ui/src/helpers/datetime/utc-date.ts
//
// Pure UTC timeline helpers (createUTCDate, formatUTCISO, add/subtract ms, getUTCNow) do not read app state.
// startOfFacilityDayUTC / endOfFacilityDayUTC call getFacilityTimeZone() (Jotai) — use only in app/test contexts
// with a hydrated store; they are unsuitable for standalone scripts or SSR without that wiring.
import { TZDate } from '@date-fns/tz';

import { createTZDate, type DateInput, getFacilityTimeZone } from './core';

/**
 * Creates a TZDate in UTC. If no date is provided, uses the current time.
 */
export function createUTCDate(date?: DateInput): TZDate {
  return createTZDate(date, 'UTC');
}

/**
 * Returns the current instant as a UTC TZDate.
 */
export const getUTCNow = (): TZDate => createUTCDate();

/**
 * Adds milliseconds to a UTC date input (or UTC now when omitted).
 */
export function addUTCMilliseconds(milliseconds: number, date?: DateInput): TZDate {
  const baseMs = (date === undefined ? getUTCNow() : createUTCDate(date)).getTime();
  return createUTCDate(baseMs + milliseconds);
}

/**
 * Subtracts milliseconds from a UTC date input (or UTC now when omitted).
 */
export function subtractUTCMilliseconds(milliseconds: number, date?: DateInput): TZDate {
  return addUTCMilliseconds(-milliseconds, date);
}

/**
 * Formats a date as an ISO 8601 UTC string (e.g. "2024-03-15T12:00:00.000Z").
 * Use when sending dates to the BFF or ECP as string payloads.
 */
export function formatUTCISO(date: DateInput): string {
  // TZDate.toISOString() can emit "+00:00" in some environments (e.g. Vitest); normalize to "Z" for consistent API payloads.
  const iso = createUTCDate(date).toISOString();
  return iso.endsWith('+00:00') ? iso.slice(0, -6) + 'Z' : iso;
}

/**
 * Returns the UTC instant for the start of the facility-local calendar day for `date`.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function startOfFacilityDayUTC(date: DateInput): TZDate {
  const facilityDate = createTZDate(date, getFacilityTimeZone());
  facilityDate.setHours(0, 0, 0, 0);
  return createUTCDate(facilityDate.getTime());
}

/**
 * Returns the UTC instant for the end of the facility-local calendar day for `date`.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function endOfFacilityDayUTC(date: DateInput): TZDate {
  const facilityDate = createTZDate(date, getFacilityTimeZone());
  facilityDate.setHours(23, 59, 59, 999);
  return createUTCDate(facilityDate.getTime());
}

/**
 * Returns the UTC instants bracketing a facility-local calendar year — start of
 * Jan 1 through end of Dec 31, both in the facility zone. Use for "list
 * everything in <year>" range queries to the BFF/ECP.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function facilityYearRangeUTC(year: number): { startTime: TZDate; endTime: TZDate } {
  const facilityTz = getFacilityTimeZone();
  // Multi-arg TZDate is the only @date-fns/tz API that builds "midnight of this
  // calendar date in this zone" without first materialising a browser-local Date.
  const jan1 = new TZDate(year, 0, 1, facilityTz);
  const dec31 = new TZDate(year, 11, 31, facilityTz);
  return {
    startTime: startOfFacilityDayUTC(jan1),
    endTime: endOfFacilityDayUTC(dec31),
  };
}

/**
 * Returns the facility-local range from the start of the current calendar year
 * (Jan 1, 00:00 facility time) through "now". Both bounds are facility-zone
 * TZDates — apply startOfFacilityDayUTC / endOfFacilityDayUTC at the call site
 * to derive the UTC instants for an API query.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function facilityYearToNowRange(): { from: TZDate; to: TZDate } {
  const facilityTz = getFacilityTimeZone();
  // Localised here so call sites don't reach for raw Date / multi-arg TZDate.
  const now = new TZDate(new Date(), facilityTz);
  const jan1 = new TZDate(now.getFullYear(), 0, 1, facilityTz);
  return { from: jan1, to: now };
}
